import { useEffect, useState, lazy, Suspense } from "react";
import {
  Routes,
  Route,
  useNavigationType,
  useLocation,
} from "react-router-dom";
import IntroLoader from "./components/IntroLoader";

const VCTLandingPage = lazy(() => import("./pages/VCTLandingPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage.js"));

/**
 * Lets anything below the curtain start its entrance when the curtain actually
 * lifts rather than when it mounts (which happens hidden behind the loader).
 * The flag exists for subscribers that mount late — the routes are lazy, so a
 * listener can easily miss the event on a session where the intro never ran.
 */
const announceIntroDone = () => {
  window.__vctIntroDone = true;
  window.dispatchEvent(new Event("vct:intro-done"));
};

function App() {
  const action = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname;
  const [isLoading, setIsLoading] = useState(() => {
    return !sessionStorage.getItem("vct-intro-seen");
  });

  useEffect(() => {
    if (action !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [action, pathname]);

  useEffect(() => {
    let title = "";
    let metaDescription = "";

    switch (pathname) {
      case "/":
        title = "Vantage Cybertech: Strategic Startup Tech Partnerships";
        metaDescription = "Strategic tech partnerships for startups. We offer freelancing, contracting, and equity-based partnerships to help you build your MVP and scale your product.";
        break;
      default:
        title = "404 :- Page Not Found";
        metaDescription = "The page you are looking for does not exist.";
        break;
    }

    if (title) {
      document.title = title;
    }

    if (metaDescription) {
      const metaDescriptionTag = document.querySelector(
        'head > meta[name="description"]'
      );
      if (metaDescriptionTag) {
        metaDescriptionTag.content = metaDescription;
      }
    }

    // Update Open Graph tags
    const ogTags = {
      'meta[property="og:title"]': title,
      'meta[property="og:description"]': metaDescription,
      'meta[property="og:url"]': window.location.href,
    };
    Object.entries(ogTags).forEach(([selector, content]) => {
      const tag = document.querySelector(selector);
      if (tag) tag.setAttribute("content", content);
    });

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", window.location.href);
  }, [pathname]);

  // A repeat visit within the session skips the loader entirely, so the signal
  // still has to fire — otherwise anything gated on it never starts. Mount-only
  // on purpose: `isLoading` is read once here, and the transition to false is
  // announced by handleIntroComplete instead.
  useEffect(() => {
    if (!isLoading) {
      announceIntroDone();
    }
  }, []);

  const handleIntroComplete = () => {
    sessionStorage.setItem("vct-intro-seen", "1");
    setIsLoading(false);
    announceIntroDone();
  };

  return (
    <>
      {isLoading && <IntroLoader onComplete={handleIntroComplete} />}
      <Suspense fallback={null}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<VCTLandingPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
